import React from 'react';
import { intersection, isUndefined, map } from 'lodash-es';
import axios from '../../configs/axios';
import { watchlistSerializedParams as prepareWatchlistParameters } from '../../utils/watchlist/prepare_watchlist_parameters';

export const MARKET_LIST_TYPE = 'market';
export const KEYWORD_LIST_TYPE = 'keywordList';

const marketConfig = {
  listName: 'Market',
  entityName: 'market',
  modalTitle: 'Add to an existing market',
  noListsMessage: 'No markets present.',
  websocketChannel: 'CustomerMarketsChannel',
  prepareParameters: prepareWatchlistParameters,
  requestList: (params) => axios.get('/my_markets/all_markets', params),
  addEntities: (marketId, params) => axios.post(`/my_markets/${marketId}/add_apps`, params),
  replaceEntities: (marketId, params) => axios.post(`/my_markets/${marketId}/replace_apps`, params),
  removeEntities: (marketId, data) => axios.delete(`/my_markets/${marketId}/remove_apps`, { data }),
  createList: (params) => axios.post('/my_markets/', params),
  createListCallbackParams: (id, listName, entities) => ({
    name: listName,
    entities: map(entities, (entity) => entity.ref_no.toString()),
    id,
  }),
  mappedLists: (markets, entities) =>
    map(markets, (market) => {
      const entitiesSum = market.entities.length + entities.length;
      return {
        disabled: market.entities.length >= WATCHLIST_LIMIT,
        added:
          intersection(
            market.entities,
            map(entities, (entity) => entity.ref_no.toString())
          ).length === entities.length,
        entitiesSum,
        entitiesDifference: entitiesSum - WATCHLIST_LIMIT,
        ...market,
      };
    }),
  mappedEntitiesKeys: (entities) => map(entities, (e) => e.ref_no.toString()),
  updateList: (stateMarkets, market, { entities, conflicts }) =>
    map(stateMarkets, (m) => {
      if (m.id === market.id) {
        const updatedMarket = { ...m };
        if (!isUndefined(entities)) updatedMarket.entities = entities;
        if (!isUndefined(conflicts)) updatedMarket.conflicts = conflicts;
        return updatedMarket;
      }

      return m;
    }),
  conflictsTypes: [
    'conflict_between_unified_app_and_app',
    'conflict_between_unified_publisher_and_publisher',
    'conflict_between_app_and_unified_app',
    'conflict_between_publisher_and_unified_publisher',
    'conflict_between_unified_app_and_unified_publisher',
    'conflict_between_unified_app_and_publisher',
    'conflict_between_app_and_unified_publisher',
    'conflict_between_app_and_publisher',
    'conflict_between_unified_app_and_company',
    'conflict_between_app_and_company',
    'conflict_between_unified_publisher_and_company',
    'conflict_between_publisher_and_company',
  ],
  renderElement: (name, conflictType, renderElementWithReplace, renderElementWithWarning) => {
    switch (conflictType) {
      case 'conflict_between_unified_app_and_app':
      case 'conflict_between_unified_publisher_and_publisher':
        return renderElementWithReplace(
          'Unified version of entity is already in this market',
          'Replace unified version'
        );
      case 'conflict_between_app_and_unified_app':
      case 'conflict_between_publisher_and_unified_publisher':
        return renderElementWithReplace(
          'Store version of entity is already in this market',
          'Replace store version'
        );
      case 'conflict_between_unified_app_and_unified_publisher':
      case 'conflict_between_unified_app_and_publisher':
      case 'conflict_between_app_and_unified_publisher':
      case 'conflict_between_app_and_publisher':
        return renderElementWithReplace(
          "A publisher's app is already in this market",
          'Replace App'
        );
      case 'conflict_between_unified_app_and_company':
      case 'conflict_between_app_and_company':
        return renderElementWithReplace("A company's app is already in this market", 'Replace App');
      case 'conflict_between_unified_publisher_and_company':
      case 'conflict_between_publisher_and_company':
        return renderElementWithReplace(
          "A company's publisher is already in this market",
          'Replace Publisher'
        );
      case 'conflict_between_unified_publisher_and_unified_app':
      case 'conflict_between_unified_publisher_and_app':
      case 'conflict_between_publisher_and_unified_app':
      case 'conflict_between_publisher_and_app':
        return renderElementWithWarning(name, 'Parent Publisher is already in this market');
      case 'conflict_between_company_and_unified_app':
      case 'conflict_between_company_and_app':
      case 'conflict_between_company_and_unified_publisher':
      case 'conflict_between_company_and_publisher':
        return renderElementWithWarning(name, 'Parent Company is already in this market');
      default:
        return renderElementWithWarning(name, 'Undefined conflict');
    }
  },
  disabledElementTooltip: (disabled, entitiesSum, entitiesDifference, listName) =>
    disabled ? (
      <div className="text-center">
        You will exceed entities limit ({entitiesSum}/{WATCHLIST_LIMIT})
        <br />
        for this {listName}.
        <br />
        Please de-select at least {entitiesDifference} entities.
      </div>
    ) : null,
};

const keywordListConfig = {
  listName: 'Keyword List',
  entityName: 'keyword',
  modalTitle: 'Add to an existing keyword list',
  noListsMessage: 'No keyword lists present.',
  websocketChannel: 'CustomerKeywordListsChannel',
  prepareParameters: (entities, keywordListParams = {}) => ({
    keyword_list: {
      ...keywordListParams,
      keywords_attributes: map(entities, (entity) => ({
        name: entity.name,
        keyword_id: entity.is_processing ? null : entity.keywordId.toString(),
      })),
    },
  }),
  requestList: () => axios.get('/keywords_lists_modals'),
  addEntities: (listId, params) =>
    axios.post(`/keywords_lists_modals/${listId}/add_keywords`, params),
  replaceEntities: () => {},
  removeEntities: (listId, data) =>
    axios.delete(`/keywords_lists_modals/${listId}/remove_keywords`, { data }),
  createList: (params) => axios.post('/keywords_lists_modals', params),
  createListCallbackParams: (id, listName, entities) => ({
    name: listName,
    entities: map(entities, (entity) => entity.name),
    id,
  }),
  mappedLists: (keywordLists, entities) =>
    map(keywordLists, (keywordList) => {
      const entitiesSum = keywordList.entities.length + entities.length;
      return {
        disabled: keywordList.entities.length >= KEYWORD_LIST_LIMIT,
        added:
          intersection(
            keywordList.entities,
            map(entities, (entity) => entity.name)
          ).length === entities.length,
        entitiesSum,
        entitiesDifference: entitiesSum - KEYWORD_LIST_LIMIT,
        ...keywordList,
      };
    }),
  mappedEntitiesKeys: (entities) => map(entities, (e) => e.name),
  updateList: (stateKeywordLists, keywordList, { entities, conflicts }) =>
    map(stateKeywordLists, (k) => {
      if (k.id === keywordList.id) {
        const updatedKeywordList = { ...k };
        if (!isUndefined(entities)) updatedKeywordList.entities = entities;
        if (!isUndefined(conflicts)) updatedKeywordList.conflicts = conflicts;
        return updatedKeywordList;
      }

      return k;
    }),
  conflictsTypes: [],
  renderElement: () => {},
  disabledElementTooltip: (disabled, entitiesSum, entitiesDifference, listName) =>
    disabled ? (
      <div className="text-center">
        You will exceed entities limit ({entitiesSum}/{KEYWORD_LIST_LIMIT})
        <br />
        for this {listName}.
        <br />
        Please de-select at least {entitiesDifference} entities.
      </div>
    ) : null,
};

const config = {
  [MARKET_LIST_TYPE]: marketConfig,
  [KEYWORD_LIST_TYPE]: keywordListConfig,
};

export default config;
